import {
  dailyDriverServiceColumns,
  dailyDriverServiceDefaultColumns,
  dailyDriverServiceSpecificFilters,
} from '@cabgo/shared';
import type { ReportViewDefinition } from '../reportViewTypes';

export const dailyDriverServiceView: ReportViewDefinition = {
  key: 'daily_driver_service',
  titleKey: 'dailyDriver_title',
  descriptionKey: 'dailyDriver_description',
  dateBasisKey: 'dailyDriver_dateBasis',
  columns: dailyDriverServiceColumns,
  defaultColumns: dailyDriverServiceDefaultColumns,
  dateMode: 'range',
  supportsSearch: true,
  defaultSort: 'businessDate',
  sorts: ['businessDate', 'agentName'],
  filters: dailyDriverServiceSpecificFilters.map((filter) => ({
    ...filter,
    labelKey: `dailyDriver_${filter.id}`,
    ...('values' in filter
      ? { options: filter.values.map((value) => ({ value, labelKey: `dailyDriver_${value}` })) }
      : {}),
  })),
  valueLabels: {
    date_basis: { SERVICE_DATE: 'dailyDriver_SERVICE_DATE' },
    scope_provenance: { CURRENT_SERVICE_GROUP: 'dailyDriver_CURRENT_SERVICE_GROUP' },
    attribution_basis: { PERSISTED_TRIP_ASSIGNEE_AS_OF: 'dailyDriver_PERSISTED_TRIP_ASSIGNEE_AS_OF' },
  },
  summary: [
    { field: 'agentCount', labelKey: 'dailyDriver_agentCount', unit: 'count' },
    { field: 'attributed_trip_count', labelKey: 'dailyDriver_attributed_trip_count', unit: 'count' },
    {
      field: 'assignment_evidence_trip_count',
      labelKey: 'dailyDriver_assignment_evidence_trip_count',
      unit: 'count',
    },
    { field: 'completed_trip_count', labelKey: 'dailyDriver_completed_trip_count', unit: 'count' },
    { field: 'offers_count', labelKey: 'dailyDriver_offers_count', unit: 'count' },
    { field: 'billed_total_sum', labelKey: 'dailyDriver_billed_total_sum', unit: 'money' },
    { field: 'received_total_sum', labelKey: 'dailyDriver_received_total_sum', unit: 'money' },
    { field: 'outstanding_total_sum', labelKey: 'dailyDriver_outstanding_total_sum', unit: 'money' },
    { field: 'unassignedTripCount', labelKey: 'dailyDriver_unassignedTripCount', unit: 'count' },
    { field: 'unassignedBilledTotal', labelKey: 'dailyDriver_unassignedBilledTotal', unit: 'money' },
    { field: 'totalRows', labelKey: 'dailyDriver_totalRows', unit: 'count' },
    { field: 'cancelled_trip_count', labelKey: 'dailyDriver_cancelled_trip_count', unit: 'count' },
    { field: 'missed_trip_count', labelKey: 'dailyDriver_missed_trip_count', unit: 'count' },
    { field: 'fare_total_sum', labelKey: 'dailyDriver_fare_total_sum', unit: 'money' },
    { field: 'free_trip_count', labelKey: 'dailyDriver_free_trip_count', unit: 'count' },
    { field: 'finance_review_count', labelKey: 'dailyDriver_finance_review_count', unit: 'count' },
    { field: 'missing_fare_count', labelKey: 'dailyDriver_missing_fare_count', unit: 'count' },
    { field: 'unassignedReceivedTotal', labelKey: 'dailyDriver_unassignedReceivedTotal', unit: 'money' },
    {
      field: 'unassignedOutstandingTotal',
      labelKey: 'dailyDriver_unassignedOutstandingTotal',
      unit: 'money',
    },
    {
      field: 'unassignedMissingFareCount',
      labelKey: 'dailyDriver_unassignedMissingFareCount',
      unit: 'count',
    },
    { field: 'planned_distance_km_sum', labelKey: 'dailyDriver_planned_distance_km_sum', unit: 'distance' },
    {
      field: 'planned_distance_known_trip_count',
      labelKey: 'dailyDriver_planned_distance_known_trip_count',
      unit: 'count',
    },
    { field: 'actual_distance_km_sum', labelKey: 'dailyDriver_actual_distance_km_sum', unit: 'distance' },
    {
      field: 'actual_distance_known_trip_count',
      labelKey: 'dailyDriver_actual_distance_known_trip_count',
      unit: 'count',
    },
    { field: 'planned_duration_min_sum', labelKey: 'dailyDriver_planned_duration_min_sum', unit: 'minutes' },
    {
      field: 'planned_duration_known_trip_count',
      labelKey: 'dailyDriver_planned_duration_known_trip_count',
      unit: 'count',
    },
    { field: 'actual_duration_min_sum', labelKey: 'dailyDriver_actual_duration_min_sum', unit: 'minutes' },
    {
      field: 'actual_duration_known_trip_count',
      labelKey: 'dailyDriver_actual_duration_known_trip_count',
      unit: 'count',
    },
    { field: 'accepted_offer_count', labelKey: 'dailyDriver_accepted_offer_count', unit: 'count' },
    { field: 'rejected_offer_count', labelKey: 'dailyDriver_rejected_offer_count', unit: 'count' },
    { field: 'expired_offer_count', labelKey: 'dailyDriver_expired_offer_count', unit: 'count' },
    { field: 'superseded_offer_count', labelKey: 'dailyDriver_superseded_offer_count', unit: 'count' },
    { field: 'cancelled_offer_count', labelKey: 'dailyDriver_cancelled_offer_count', unit: 'count' },
    { field: 'pending_offer_count', labelKey: 'dailyDriver_pending_offer_count', unit: 'count' },
  ],
  primarySummary: [
    'agentCount',
    'attributed_trip_count',
    'completed_trip_count',
    'offers_count',
    'billed_total_sum',
    'outstanding_total_sum',
  ],
  summaryGroups: [
    {
      labelKey: 'summaryGroupOverview',
      fields: [
        'agentCount',
        'totalRows',
        'attributed_trip_count',
        'completed_trip_count',
        'cancelled_trip_count',
        'missed_trip_count',
      ],
    },
    {
      labelKey: 'summaryGroupFinance',
      fields: [
        'fare_total_sum',
        'billed_total_sum',
        'received_total_sum',
        'outstanding_total_sum',
        'free_trip_count',
        'finance_review_count',
      ],
    },
    {
      labelKey: 'summaryGroupDispatch',
      fields: [
        'offers_count',
        'accepted_offer_count',
        'rejected_offer_count',
        'expired_offer_count',
        'superseded_offer_count',
        'cancelled_offer_count',
        'pending_offer_count',
      ],
    },
    {
      labelKey: 'summaryGroupRouting',
      fields: [
        'planned_distance_km_sum',
        'planned_distance_known_trip_count',
        'actual_distance_km_sum',
        'actual_distance_known_trip_count',
        'planned_duration_min_sum',
        'planned_duration_known_trip_count',
        'actual_duration_min_sum',
        'actual_duration_known_trip_count',
      ],
    },
    {
      labelKey: 'summaryGroupEvidence',
      fields: [
        'assignment_evidence_trip_count',
        'missing_fare_count',
        'unassignedTripCount',
        'unassignedBilledTotal',
        'unassignedReceivedTotal',
        'unassignedOutstandingTotal',
        'unassignedMissingFareCount',
      ],
    },
  ],
};
